import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Heart, MessageSquareQuote, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  referralSchema,
  testimonialSchema,
  type ReferralValues,
  type TestimonialValues,
} from "@/lib/postSaleSchema";
import postSaleService from "@/services/postSaleService";
import { validateAndProcessImage } from "@/utils/imageValidator";

/**
 * Página pública de postventa (HU-20), abierta desde el enlace del correo de
 * reconocimiento. El cliente cerrado puede dejar un testimonio (para la
 * prueba social, que se revisa antes de publicar) y recomendar a alguien. El
 * token de la URL identifica la cotización; uno inválido responde neutro.
 */
const PostSale = () => {
  const { token = "" } = useParams();
  const { toast } = useToast();
  const [proofImage, setProofImage] = useState<string | undefined>(undefined);
  const [imageError, setImageError] = useState<string | undefined>(undefined);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["postventa", token],
    queryFn: () => postSaleService.getContext(token),
    enabled: Boolean(token),
    retry: false,
  });

  const testimonialForm = useForm<TestimonialValues>({
    resolver: zodResolver(testimonialSchema),
    defaultValues: { text: "", allowPublish: false },
  });

  const referralForm = useForm<ReferralValues>({
    resolver: zodResolver(referralSchema),
    defaultValues: { name: "", phone: "", email: "", company: "" },
  });

  const onProofImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const result = await validateAndProcessImage(file);
    if (!result.valid) {
      setProofImage(undefined);
      setImageError(
        result.errors[0]?.message ?? "No pudimos usar esa imagen."
      );
      return;
    }

    setImageError(undefined);
    setProofImage(result.processedImage);
  };

  const onSubmitTestimonial = testimonialForm.handleSubmit(async (values) => {
    try {
      await postSaleService.submitTestimonial(token, {
        text: values.text ?? "",
        allowPublish: values.allowPublish ?? false,
        image: proofImage,
      });
      toast({ title: "¡Gracias!", description: "Recibimos tu testimonio." });
      testimonialForm.reset();
      setProofImage(undefined);
    } catch {
      toast({
        variant: "destructive",
        title: "No pudimos guardar tu testimonio",
        description: "Probá de nuevo en unos minutos.",
      });
    }
  });

  const onSubmitReferral = referralForm.handleSubmit(async (values) => {
    try {
      await postSaleService.submitReferral(token, {
        name: values.name ?? "",
        phone: values.phone ?? "",
        email: values.email ?? "",
        company: values.company ?? "",
      });
      toast({
        title: "¡Gracias por recomendar!",
        description: "Nos pondremos en contacto con tu referido.",
      });
      referralForm.reset();
    } catch {
      toast({
        variant: "destructive",
        title: "No pudimos guardar tu recomendación",
        description: "Probá de nuevo en unos minutos.",
      });
    }
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center text-muted-foreground">
        Cargando…
      </div>
    );
  }

  if (isError || !data?.ok) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16">
        <Card>
          <CardContent className="pt-6 text-center text-muted-foreground">
            Este enlace ya no es válido o expiró. Si querés dejarnos tu
            testimonio o recomendar a alguien, escribinos y te ayudamos.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-8 px-4 py-16">
      <header className="space-y-2 text-center">
        <Heart className="mx-auto h-8 w-8 text-primary" />
        <h1 className="text-2xl font-semibold">
          Gracias{data.name ? `, ${data.name}` : ""}
        </h1>
        <p className="text-muted-foreground">
          Fue un gusto trabajar con vos. Si te gustó el resultado, podés dejarnos
          un testimonio o recomendar a alguien que también lo necesite.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquareQuote className="h-5 w-5" /> Dejá tu testimonio
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={onSubmitTestimonial}
            className="space-y-4"
            noValidate
          >
            <div className="space-y-2">
              <Label htmlFor="testimonial-text">Tu experiencia</Label>
              <Textarea
                id="testimonial-text"
                rows={4}
                {...testimonialForm.register("text")}
              />
              {testimonialForm.formState.errors.text && (
                <p className="text-sm text-destructive">
                  {testimonialForm.formState.errors.text.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="testimonial-image">
                Imagen de prueba (opcional)
              </Label>
              <Input
                id="testimonial-image"
                type="file"
                accept="image/*"
                onChange={onProofImageChange}
              />
              {imageError && (
                <p className="text-sm text-destructive">{imageError}</p>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                {...testimonialForm.register("allowPublish")}
              />
              Autorizo que publiquen mi testimonio en el sitio.
            </label>

            <Button
              type="submit"
              disabled={testimonialForm.formState.isSubmitting}
            >
              Enviar testimonio
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" /> ¿A quién conocés que necesite esto?
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmitReferral} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="referral-name">Nombre</Label>
              <Input id="referral-name" {...referralForm.register("name")} />
              {referralForm.formState.errors.name && (
                <p className="text-sm text-destructive">
                  {referralForm.formState.errors.name.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="referral-phone">Teléfono</Label>
              <Input id="referral-phone" {...referralForm.register("phone")} />
              {referralForm.formState.errors.phone && (
                <p className="text-sm text-destructive">
                  {referralForm.formState.errors.phone.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="referral-email">Email (opcional)</Label>
              <Input
                id="referral-email"
                type="email"
                {...referralForm.register("email")}
              />
              {referralForm.formState.errors.email && (
                <p className="text-sm text-destructive">
                  {referralForm.formState.errors.email.message}
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={referralForm.formState.isSubmitting}
            >
              Recomendar
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default PostSale;
